import { createConfig, http, injected } from 'wagmi';
import type { CreateConnectorFn } from 'wagmi';
import type { Logger } from 'pino';
import { rootstock } from './chains';
import { createWalletConnectLogger } from './walletConnectLogger';

const walletConnectProjectId = (
  import.meta.env.VITE_WALLETCONNECT_PROJECT_ID as string | undefined
)?.trim();
const fallbackAppUrl = (import.meta.env.VITE_APP_URL as string | undefined)?.trim() || 'https://dca.bitchill.app';
const rootstockPublicRpcUrl = rootstock.rpcUrls.default.http[0];

function rootstockReadTransport() {
  const proxyTransport = http('/api/rpc');

  return ((params) => {
    const transport = proxyTransport(params);

    return {
      ...transport,
      value: {
        ...(transport.value ?? {}),
        // WalletConnect inspects viem transport.value.url via wagmi's extractRpcUrls().
        // Keep app reads on /api/rpc, but advertise an absolute RPC URL for connector setup.
        url: rootstockPublicRpcUrl,
      },
    };
  }) satisfies ReturnType<typeof http>;
}

function getWalletConnectMetadata() {
  const origin = typeof window !== 'undefined' && window.location.origin
    ? window.location.origin
    : fallbackAppUrl;

  return {
    name: 'BitChill',
    description: 'BitChill DCA frontend',
    url: origin,
    icons: [`${origin}/bitchill_fav.png`],
  };
}

export const wagmiConfig = createConfig({
  chains: [rootstock],
  // multiInjectedProviderDiscovery (default: true) auto-discovers wallets via EIP-6963.
  // Keep a single injected() connector as fallback for non-EIP-6963 wallets.
  // WalletConnect is lazy-added at runtime via ensureWalletConnectConnector().
  connectors: [injected({ shimDisconnect: true })],
  transports: {
    [rootstock.id]: rootstockReadTransport(),
  },
  ssr: false,
});

type RuntimeConnectorFactory = Parameters<typeof wagmiConfig._internal.connectors.setup>[0];
let walletConnectFactoryPromise: Promise<RuntimeConnectorFactory | null> | null = null;

export function hasWalletConnectProjectId(): boolean {
  return Boolean(walletConnectProjectId);
}

export async function ensureWalletConnectConnector(): Promise<CreateConnectorFn | null> {
  if (!walletConnectProjectId) return null;

  if (!walletConnectFactoryPromise) {
    walletConnectFactoryPromise = (async () => {
      const { walletConnect } = await import('wagmi/connectors');

      // eslint-disable-next-line @typescript-eslint/no-deprecated -- temporary until wagmi exposes a non-deprecated WalletConnect connector
      const connectorFn = walletConnect({
        projectId: walletConnectProjectId,
        showQrModal: true,
        // Workaround for WalletConnect logger/pino browser incompatibility.
        // Ensures child loggers always expose `bindings()` as a function.
        logger: createWalletConnectLogger('walletconnect') as unknown as Logger,
        metadata: getWalletConnectMetadata(),
      });

      return connectorFn;
    })().catch((error: unknown) => {
      walletConnectFactoryPromise = null;
      throw error;
    });
  }

  return walletConnectFactoryPromise;
}
